import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { useGamification } from '@/hooks/useGamification';

const MILESTONE_KEY = 'sayitnow_last_milestone_shown';

const milestones = [
  {
    threshold: 50,
    en: "You're becoming someone who doesn't wait.",
    es: 'Te estás convirtiendo en alguien que no espera.',
    fr: 'Vous devenez quelqu\'un qui n\'attend pas.',
    pt: 'Você está se tornando alguém que não espera.',
    ar: 'أنت تصبح شخصاً لا ينتظر.',
    ja: 'あなたは待たない人になりつつあります。',
    ko: '당신은 기다리지 않는 사람이 되어가고 있습니다.',
    id: 'Kamu menjadi seseorang yang tidak menunggu.',
  },
  {
    threshold: 100,
    en: "You're the kind of person who shows up.",
    es: 'Eres el tipo de persona que se presenta.',
    fr: 'Vous êtes le genre de personne qui se manifeste.',
    pt: 'Você é o tipo de pessoa que aparece.',
    ar: 'أنت من النوع الذي يظهر دائماً.',
    ja: 'あなたはちゃんと現れる人です。',
    ko: '당신은 나타나는 사람입니다.',
    id: 'Kamu adalah tipe orang yang hadir.',
  },
  {
    threshold: 250,
    en: "You're building something that lasts.",
    es: 'Estás construyendo algo que perdura.',
    fr: 'Vous construisez quelque chose qui dure.',
    pt: 'Você está construindo algo que dura.',
    ar: 'أنت تبني شيئاً يدوم.',
    ja: 'あなたは永く続くものを築いています。',
    ko: '당신은 오래 남을 무언가를 만들고 있습니다.',
    id: 'Kamu sedang membangun sesuatu yang abadi.',
  },
];

const copy = {
  en: { dismiss: 'Continue' },
  es: { dismiss: 'Continuar' },
  fr: { dismiss: 'Continuer' },
  pt: { dismiss: 'Continuar' },
  ar: { dismiss: 'متابعة' },
  ja: { dismiss: '続ける' },
  ko: { dismiss: '계속' },
  id: { dismiss: 'Lanjutkan' },

  hi: { dismiss: 'जारी रखें' },
};

export function IdentityMilestone() {
  const [message, setMessage] = useState<string | null>(null);
  const { language } = useLanguage();
  const t = lc(copy, language);
  const { userLevel } = useGamification();

  const points = userLevel?.total_points ?? 0;

  useEffect(() => {
    const lastShown = parseInt(localStorage.getItem(MILESTONE_KEY) || '0');

    // Find the highest threshold the user has passed that hasn't been shown
    for (let i = milestones.length - 1; i >= 0; i--) {
      const m = milestones[i];
      if (points >= m.threshold && lastShown < m.threshold) {
        // Use lc-style fallback: try current language, fall back to en
        const msg = (m as Record<string, unknown>)[language] as string | undefined;
        setMessage(msg ?? m.en);
        localStorage.setItem(MILESTONE_KEY, m.threshold.toString());
        break;
      }
    }
  }, [points, language]);

  if (!message) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="mb-6 text-center py-6 px-6 rounded-3xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/15"
      >
        <p className="text-lg font-heading font-semibold text-foreground mb-4">
          ✨ {message}
        </p>
        <button
          onClick={() => setMessage(null)}
          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          {t.dismiss}
        </button>
      </motion.div>
    </AnimatePresence>
  );
}
